import { WrapResponse } from '@/types';
import { fun } from '@/lib/request';
import { api } from '@/lib/env';
import { StateSlice } from '@/stores/appStore';
import { toast } from 'react-hot-toast';
import { snakeToCamel } from '@/lib/utils';
import { Note, NoteId, Notes } from '@/stores/context/createNoteStore';
import dayjs from 'dayjs';


export type SubjectId = string;
export type Subject = {
  id: SubjectId;
  subjectName: string;
  updateTime: string;
  paperNum: number;
  noteNum: number;
}

type SubjectData = Subject & {
  creatTime: string;
  updateTime: string;
  userId: number;
}
type SubjectListData = Subject[];
type Processing = '' | 'deleting' | 'creating' | 'renaming';
type OmitNote = Omit<Note, 'viewer' | 'type'>;
type KnowledgeFrameData = {
  title?: string;
  key?: string;
  content: OmitNote[],
}

const namespace = 'subjects';
export type SubjectSlice = {
  [namespace]: {
    expandeds: string[];
    onFullExpanded: (key: string, fullExpanded?: boolean) => void;
    onExpanded: (key: string, expanded: boolean) => void;
    isExpanded: (key: string) => boolean;
    isFullExpanded: (key: string) => boolean;
    subjects: Subject[];
    subjectsFetched: boolean;
    fetchSubjectsLoading: boolean;
    fetchSubjects: (silent?: boolean) => Promise<void>;
    fetchSubject: (subjectId: string) => Promise<Subject | null>;
    processing: Processing;
    createSubject: (subjectName: string, silent?: boolean) => Promise<string>;
    renameSubject: (subjectId: string, subjectName: string) => Promise<void>;
    deleteSubject: (subjectId: string) => Promise<void>;
    importPaper: (subjectId: string, paperIds: string[], silent?: boolean) => Promise<void>;
    removePaper: (subjectId: string, paperIds: string[]) => Promise<void>;
    knowledgeFrame: {
      default: {
        key: string;
        notes: Notes;
        title: string;
      } | null;
      current: {
        key: string;
        notes: Notes;
        title: string;
      } | null;
    };
    insertKnowledgeFrameToNote: (subjectId: string, key: string) => Promise<void>;
    fetchKnowledgeFrameLoading: boolean;
    fetchKnowledgeFrame: (research?: {
      direction: string;
      complement: string;
    } | null) => Promise<{
      code: number;
      message: string;
    }>;
  }
};

export const createSubjectSlice: StateSlice<SubjectSlice> = ((set, _get) => {
  const get = () => _get()[namespace];
  const fetchPapers = (subjectId?: string, silent = false) => _get().fetchPapers(subjectId, silent);
  const TOAST_ID = 'subject-processing';
  let lastCollapsed = '';
  const setProcessing = (processing: Processing) => {
    set(({ [namespace]: state }) => {
      state.processing = processing;
    });
  };
  return {
    [namespace]: {
      expandeds: ['research'],
      // 全展开或只展开一个
      onFullExpanded: (key, fullExpanded = true) => {
        const expandeds = get().expandeds;
        const es = expandeds.filter((item) => item !== key);
        if (fullExpanded) {
          // 只展开当前 key，其他全部收起
          set(({ [namespace]: state }) => {
            lastCollapsed = es[0] || key; // 记录上一个被收起的 key
            state.expandeds = [key];
          });
        } else {
          // 恢复上一次被收起的 key
          set(({ [namespace]: state }) => {
            state.expandeds.push(lastCollapsed);
          });
        }
      },
      // 控制单个 key 的展开/收起，最多允许两个展开
      onExpanded: (key, expanded) => {
        const expandeds = get().expandeds;
        const es = expandeds.filter((item) => item !== key);
        if (expanded) {
          // 展开：加入 key
          es.push(key);
        }
        // 最多只保留两个展开项
        while (es.length > 2) {
          es.shift();
        }
        // 如果全部收起，则恢复上一次被收起的 key
        if (es.length < 1) {
          es.push(lastCollapsed);
        }
        set(({ [namespace]: state }) => {
          if (!expanded) {
            lastCollapsed = key; // 记录最后一次收起的 key
          }
          state.expandeds = es;
        });
      },
      // 判断某个 key 是否已展开
      isExpanded: (key: string) => {
        return get().expandeds.includes(key);
      },
      // 判断某个 key 是否为"全展开"状态（只展开了它自己）
      isFullExpanded: (key: string) => {
        const { expandeds } = get();
        return expandeds.includes(key) && expandeds.length === 1;
      },
      subjects: [],
      subjectsFetched: false,
      fetchSubjectsLoading: true,
      fetchSubjects: async (silent = false) => {
        if (!silent) {
          set(({ [namespace]: state }) => {
            state.fetchSubjectsLoading = true;
          });
        }
        try {
          const formData = new FormData();
          const { data, code, message: msg } = (
            await fun.post<WrapResponse<SubjectListData>>(
              api('/subject/list', true),
              formData,
            )
          ).data;
          if (code === 0) {
            const subjects = snakeToCamel<SubjectListData>(data);
            set(({ [namespace]: state }) => {
              state.subjects = [...subjects];
              state.subjectsFetched = true;
            });
          } else {
            toast.error(msg);
          }
        } catch (error) {
          console.error('Error fetch subject:', error);
        } finally {
          if (!silent) {
            set(({ [namespace]: state }) => {
              state.fetchSubjectsLoading = false;
            });
          }
        }
      },
      processing: '',
      createSubject: async (subjectName, silent = false) => {
        setProcessing('creating');
        if (!silent) {
          toast.loading('主题分类创建中', {
            id: TOAST_ID,
          });
        }
        try {
          const formData = new FormData();
          formData.append('subject_name', subjectName);
          const { data, code, message: msg } = (
            await fun.post<WrapResponse<SubjectData>>(
              api('/subject/create', true),
              formData,
            )
          ).data;
          if (code === 0) {
            await get().fetchSubjects(true);
            if (!silent) {
              toast.success('主题分类创建成功');
            }
            return data.id.toString();
          } else {
            toast.error(`主题分类创建失败，请稍后重试：${msg}`);
          }
          return '';
        } catch (error) {
          console.error('Error create subject:', error);
          return '';
        } finally {
          if (!silent) {
            toast.dismiss(TOAST_ID);
          }
          setProcessing('');
        }
      },
      renameSubject: async (subjectId, subjectName) => {
        setProcessing('renaming');
        toast.loading('主题分类重命名中', {
          id: TOAST_ID,
        });
        try {
          const formData = new FormData();
          formData.append('subject_id', subjectId);
          formData.append('subject_name', subjectName);
          const { code, message: msg } = (
            await fun.post<WrapResponse<SubjectData>>(
              api('/subject/rename', true),
              formData,
            )
          ).data;
          if (code === 0) {
            await get().fetchSubjects(true);
            toast.success('主题分类重命名成功');
          } else {
            toast.error('主题分类重命名失败，请稍后重试');
          }
        } catch (error) {
          console.error('Error rename subject:', error);
        } finally {
          toast.dismiss(TOAST_ID);
          setProcessing('');
        }
      },
      deleteSubject: async (subjectId) => {
        setProcessing('deleting');
        toast.loading('主题分类删除中', {
          id: TOAST_ID,
        });
        try {
          const formData = new FormData();
          formData.append('subject_id', subjectId);
          const { code, message: msg } = (
            await fun.post<WrapResponse<SubjectData>>(
              api('/subject/delete', true),
              formData,
            )
          ).data;
          if (code === 0) {
            await get().fetchSubjects(true);
            toast.success('主题分类删除成功');
          } else {
            toast.error('主题分类删除失败，请稍后重试');
          }
        } catch (error) {
          console.error('Error delete subject:', error);
        } finally {
          toast.dismiss(TOAST_ID);
          setProcessing('');
        }
      },
      fetchSubject: async (subjectId) => {
        const { subjects } = get();
        if (!subjects.length) {
          await get().fetchSubjects(true);
        }
        return get().subjects.find(({ id }) => id === subjectId) || null;
      },
      importPaper: async (subjectId, paperIds, silent = false) => {
        set(state => {
          if (!silent) {
            state.fetchPapersLoading = true;
          }
        });
        try {
          const formData = new FormData();
          formData.append('subject_id', subjectId);
          paperIds.forEach((paperId, i) => {
            formData.append(`paper_ids[${i}]`, paperId);
          })
          const { code, message: msg } = (
            await fun.post<WrapResponse<{}>>(
              api('/subject/importPaper', true),
              formData,
            )
          ).data;
          if (code === 0) {
            if (!silent) {
              await fetchPapers(subjectId, true);
              toast.success('导入文献成功');
            }
          } else {
            toast.error('导入文献失败，请稍后重试');
          }
        } catch (error) {
          console.error('Error import subject paper', error);
        } finally {
          set(state => {
            if (!silent) {
              state.fetchPapersLoading = false;
            }
          });
        }
      },
      removePaper: async (subjectId, paperIds) => {
        try {
          const formData = new FormData();
          formData.append('subject_id', subjectId);
          paperIds.forEach((paperId, i) => {
            formData.append(`paper_ids[${i}]`, paperId);
          })
          const { code, message: msg } = (
            await fun.post<WrapResponse<{}>>(
              api('/subject/removePaper', true),
              formData,
            )
          ).data;
          if (code === 0) {
            await fetchPapers(subjectId);
            toast.success('移除文献成功');
          } else {
            toast.error('移除文献失败，请稍后重试');
          }
        } catch (error) {
          console.error('Error remove subject paper', error);
        } finally {
        }
      },
      knowledgeFrame: {
        default: null,
        current: null,
      },
      insertKnowledgeFrameToNote: async (subjectId, key) => {
        toast.loading('插入主题笔记中', {
          id: TOAST_ID,
        });
        try {
          const formData = new FormData();
          formData.append('subject_id', subjectId);
          formData.append('key', key);
          const { code, message: msg } = (
            await fun.post<WrapResponse<{}>>(
              api('/subject/knowledge_frame_to_note', true),
              formData,
            )
          ).data;
          if (code === 0) {
            await fetchPapers(subjectId);
            toast.success('插入主题笔记成功');
          } else {
            toast.error('插入主题笔记失败，请稍后重试');
          }
        } catch (error) {
          console.error('Error import knowledge frame to note', error);
        } finally {
          toast.dismiss(TOAST_ID);
        }
      },
      fetchKnowledgeFrameLoading: false,
      fetchKnowledgeFrame: async (research) => {
        const defaultKnowledgeFrame = get().knowledgeFrame.default;
        if (!research && defaultKnowledgeFrame) {
          return {
            code: 0,
            message: '',
          };
        }
        if (research) {
          set(({ [namespace]: state }) => {
            state.fetchKnowledgeFrameLoading = true;
          });
        }
        try {
          const formData = new FormData();
          if (research) {
            const {
              direction: d,
              complement: c,
            } = research;
            d && formData.append('research_dc', d);
            c && formData.append('complete_information', c);
          }

          const { data, code, message } = (research ?
            await fun.post<WrapResponse<KnowledgeFrameData>>(
              api('/subject/knowledge_frame', true),
              formData,
            ) :
            await fun.post<WrapResponse<KnowledgeFrameData>>(
              api('/subject/knowledge_frame_default_view', true),
              formData,
            )
          ).data;
          if (code === 0) {
            const root: Note = {
              id: '0',
              pid: '',
              children: [],
              content: '',
              type: 'comment',
              viewer: {},
              images: [],
            };
            const entities: Record<NoteId, Note> = {
              [root.id]: root,
            };
            data.content.forEach((note) => {
              if (note.pid === root.id) {
                root.children.push(note.id);
              }
              entities[note.id] = {
                ...note,
                type: 'comment',
                viewer: {},
              };
            });
            const knowledgeFrame = {
              title: data.title || research?.direction || '',
              key: data.key || '',
              notes: {
                rootId: root.id,
                entities,
              },
            };
            set(({ [namespace]: state }) => {
              if (research) {
                state.knowledgeFrame.current = knowledgeFrame;
              } else {
                state.knowledgeFrame.default = knowledgeFrame;
              }
            });
            _get().refreshUserInfo();
          } else if (code === 5001) {
            _get().setBalanceInsufficient(true);
          }
          return {
            code,
            message,
          };
        } catch (error) {
          console.error('Error fetch knowledge frame:', error);
          return {
            code: -1,
            message: '请求失败，请稍后重试',
          };
        } finally {
          if (research) {
            set(({ [namespace]: state }) => {
              state.fetchKnowledgeFrameLoading = false;
            });
          }
        }
      },
    }
  }
});
